import { Box } from '@doda/io-react/common';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './index.module.scss';

/**
 * Props
 * @param children 子要素
 * @param isTopFollowing 要素が画面上部にいるか
 * @param isScrollButtonUp Is the element following bottom banner?
 */
interface Props {
  children: ReactNode;
  isTopFollowing?: boolean;
  isScrollButtonUp?: boolean;
}

const topFixedSidebarDistance = 100; // サイドバー画面上部に固定時の上余白
const bottomFixedSidebarDistance = 10; // サイドバー画面下部に固定時の下余白

export const SearchSideBarLayout: FC<Props> = ({
  children,
  isTopFollowing = false,
  isScrollButtonUp,
}) => {
  const sidebarContainerRef = useRef<HTMLDivElement | null>(null);
  const sidebarAreaRef = useRef<HTMLDivElement | null>(null);
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const prevScrollY = useRef(0);
  const topScrollY = useRef(0);
  const bottomScrollY = useRef(0);

  const [isTopFixed, setIsTopFixed] = useState<boolean>();
  const [isBottomFixed, setIsBottomFixed] = useState<boolean>();
  const [isScrollingUp, setIsScrollingUp] = useState<boolean>();
  const [isScrollingDown, setIsScrollingDown] = useState<boolean>();
  const [isBottomWindowScrolled, setIsBottomWindowScrolled] =
    useState<boolean>();
  const [headerHeight, setHeaderHeight] = useState<number>();

  useEffect(() => {
    // 初期表示時にサイドバー上端と画面上端の差を取得（ヘッダーとパンくず分の高さ）
    const sidebarArea = sidebarAreaRef.current;
    const sidebarAreaRect = sidebarArea?.getBoundingClientRect();
    setHeaderHeight(sidebarAreaRect?.top);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (isTopFollowing) return;

      const sidebarContainer = sidebarContainerRef.current;
      const sidebarArea = sidebarAreaRef.current;
      const sidebar = sidebarRef.current;

      // 各サイドバーのDOM等を取得できているか判定
      if (!sidebar || !sidebarContainer || !sidebarArea || !headerHeight)
        return;

      // 画面右側のコンテンツ高さがサイドバーの高さ以上か判定
      if (sidebarContainer.offsetHeight <= sidebar.offsetHeight + 2) {
        // Box(IOコンポーネント)のボーダーの太さ分だけsidebar.offsetHeightにプラスする（上下合わせて2px分）
        return;
      }

      const currentScrollY = window.scrollY;
      const sidebarContainerRect = sidebarContainer.getBoundingClientRect();
      const sidebarRect = sidebar.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // ページ上部までスクロールしたらリセット
      if (currentScrollY <= headerHeight) {
        setIsTopFixed(false);
        setIsBottomFixed(false);
        setIsScrollingUp(false);
        setIsScrollingDown(false);
        setIsBottomWindowScrolled(false);
        sidebarArea.style.top = `auto`;
        sidebarArea.style.transform = '';
        prevScrollY.current = currentScrollY;
        return;
      }

      // 画面下に固定するか判定
      if (
        !isBottomFixed &&
        !isTopFixed &&
        !isScrollingUp &&
        !isScrollingDown &&
        !isBottomWindowScrolled &&
        sidebarRect.bottom <= windowHeight &&
        sidebarRect.bottom >= 0
      ) {
        setIsBottomFixed(true);
        sidebarArea.style.top = `auto`;
        sidebarArea.style.transform = '';
        prevScrollY.current = currentScrollY;
        return;
      }

      // スクロールの向き判定
      const scrollingUp = currentScrollY < prevScrollY.current;
      const scrollingDown = currentScrollY > prevScrollY.current;

      // 画面下に固定されている状態で上スクロール
      if (isBottomFixed && scrollingUp) {
        bottomScrollY.current = currentScrollY;
        sidebarArea.style.transform = `translateY(${
          sidebarRect.top + window.scrollY - headerHeight
        }px)`;
        sidebarArea.style.top = `auto`;
        prevScrollY.current = currentScrollY;
        setIsTopFixed(false);
        setIsBottomFixed(false);
        setIsScrollingUp(true);
        setIsScrollingDown(false);
        return;
      }

      // 画面下に固定されている状態からサイドバー最上部までスクロールされた場合、画面上に固定
      if (
        isScrollingUp &&
        bottomScrollY.current -
          (sidebar.offsetHeight - window.innerHeight) -
          topFixedSidebarDistance >=
          currentScrollY
      ) {
        sidebarArea.style.top = 'auto';
        sidebarArea.style.transform = '';
        prevScrollY.current = currentScrollY;
        setIsTopFixed(true);
        setIsBottomFixed(false);
        setIsScrollingUp(false);
        setIsScrollingDown(false);
        return;
      }

      // 画面上に固定されている状態からサイドバー最下部までスクロールされた場合、画面下に固定
      if (
        isScrollingDown &&
        topScrollY.current +
          (sidebar.offsetHeight - window.innerHeight) +
          bottomFixedSidebarDistance <=
          currentScrollY
      ) {
        sidebarArea.style.top = 'auto';
        sidebarArea.style.transform = '';
        prevScrollY.current = currentScrollY;
        setIsTopFixed(false);
        setIsBottomFixed(true);
        setIsScrollingUp(false);
        setIsScrollingDown(false);
        return;
      }

      // 画面上に固定されている状態で下スクロール
      if (isTopFixed && scrollingDown) {
        topScrollY.current = currentScrollY;
        sidebarArea.style.top = `auto`;
        sidebarArea.style.transform = `translateY(${
          prevScrollY.current - topFixedSidebarDistance
        }px)`;
        prevScrollY.current = currentScrollY;
        setIsTopFixed(false);
        setIsBottomFixed(false);
        setIsScrollingUp(false);
        setIsScrollingDown(true);
        return;
      }

      // 画面下に固定されている状態で上スクロールし、再度下スクロールで画面下に固定
      if (isScrollingUp && bottomScrollY.current <= currentScrollY) {
        prevScrollY.current = currentScrollY;
        sidebarArea.style.transform = '';
        setIsTopFixed(false);
        setIsBottomFixed(true);
        setIsScrollingUp(false);
        setIsScrollingDown(false);
        return;
      }

      // 画面上に固定されている状態で下スクロールし、再度上スクロールで画面上に固定
      if (isScrollingDown && topScrollY.current >= currentScrollY) {
        sidebarArea.style.top = 'auto';
        sidebarArea.style.transform = '';
        prevScrollY.current = currentScrollY;
        setIsTopFixed(true);
        setIsBottomFixed(false);
        setIsScrollingUp(false);
        setIsScrollingDown(false);
        return;
      }

      // 画面最下部までスクロール
      if (
        !isBottomWindowScrolled &&
        sidebarContainerRect.bottom - sidebarRect.bottom <= 1
      ) {
        bottomScrollY.current = currentScrollY;
        sidebarArea.style.transform = `translateY(${
          sidebarContainer.offsetHeight - sidebar.offsetHeight
        }px)`;
        prevScrollY.current = currentScrollY;
        setIsTopFixed(false);
        setIsBottomFixed(false);
        setIsScrollingUp(false);
        setIsScrollingDown(false);
        setIsBottomWindowScrolled(true);
        return;
      }

      // 画面最下部までスクロール後、サイドバー上部までスクロールでサイドバーを画面上に固定
      if (
        isBottomWindowScrolled &&
        sidebarContainer.offsetHeight -
          sidebar.offsetHeight +
          topFixedSidebarDistance >=
          currentScrollY
      ) {
        sidebarArea.style.top = `auto`;
        sidebarArea.style.transform = '';
        prevScrollY.current = currentScrollY;
        setIsTopFixed(true);
        setIsBottomFixed(false);
        setIsScrollingUp(false);
        setIsScrollingDown(false);
        setIsBottomWindowScrolled(false);
        return;
      }

      // 現在のスクロール量を保存
      prevScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [
    headerHeight,
    isBottomFixed,
    isBottomWindowScrolled,
    isScrollingDown,
    isScrollingUp,
    isTopFixed,
    isTopFollowing,
    isScrollButtonUp,
  ]);

  return (
    <div className={styles.sidebar__container} ref={sidebarContainerRef}>
      <div
        className={classNames(
          styles.sidebar__area,
          {
            [styles.isBottomFixed]: isBottomFixed,
          },
          {
            [styles.isTopFixed]: isTopFixed || isTopFollowing,
          }
        )}
        ref={sidebarAreaRef}
      >
        <Box
          className={classNames(styles.sidebar__wrapper, {
            [styles['sidebar__wrapper--scrollButtonUp']]: isScrollButtonUp,
          })}
          border="gray"
          radius="4"
        >
          <div className={styles.sidebar__inner} ref={sidebarRef}>
            {children}
          </div>
        </Box>
      </div>
    </div>
  );
};
